import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CanComponentDeactivate } from '../../guards/unsaved-changes.guard';
import { UnsavedChangesTrackerService } from '../../services/unsaved-changes/unsaved-changes-tracker.service';
import { UnsavedChangesHostHelper } from '../../services/unsaved-changes/unsaved-changes-host.helper';
import {
  ColDef,
  GridReadyEvent,
  IDatasource,
  IGetRowsParams,
  PaginationChangedEvent,
} from 'ag-grid-community';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { RouterLink, ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { get } from 'lodash';
import { forkJoin } from 'rxjs';
import { RolesActionCellRendererComponent } from '../roles-management/roles-action-cell-renderer.component';
import { UserRolesCellRendererComponent } from './user-roles-cell-renderer.component';
import {
  SortDirection,
  UserListFilter,
  UserListRow,
  UserListSearchPayload,
  UserService,
} from '../../services/user-service/user.service';
import {
  AddUserDialogData,
  AddUserDialogResult,
  AddUserModalComponent,
} from '../add-user-modal/add-user-modal.component';
import { ConfirmDeleteUserComponent } from '../confirm-delete-user/confirm-delete-user.component';
import {
  AG_GRID_DEFAULT_PAGE_SIZE,
  AG_GRID_PAGE_SIZE_OPTIONS,
  AG_GRID_EMPTY_BODY_ROW_PX,
  AG_GRID_HEADER_AND_PAGING_PX,
  syncInfiniteRowModelPageSize,
} from '../../shared/ag-grid/ag-grid-pagination.util';
import { HasPermissionDirective } from '../../directives/has-permission.directive';
import { RbacPermissions } from '../../services/rbac/rbac-permissions';
import { RbacService } from '../../services/rbac/rbac.service';
import { AgGridCopyCellTooltipDirective } from '../../shared/ag-grid/ag-grid-copy-cell-tooltip.directive';
import { createAgGridSelectFloatingFilter } from '../../shared/ag-grid/select-floating-filter';
import {
  createUtcDateFilterParams,
  formatUtcDateTimeCell,
  mapAgGridFilterToApi,
  withDateColumnLayout,
} from '../../shared/ag-grid/ag-grid-date-column.util';
import { FormsModule } from '@angular/forms';
import { CustomerService } from '../../services/customer-service/customer.service';

const UserStatusFloatingFilter = createAgGridSelectFloatingFilter([
  { label: 'All', value: '' },
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' },
]);

@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    AgGridAngular,
    AgGridModule,
    RolesActionCellRendererComponent,
    UserRolesCellRendererComponent,
    HasPermissionDirective,
    AgGridCopyCellTooltipDirective,
  ],
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss',
})
export class UserListComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  readonly RbacPermissions = RbacPermissions;

  private readonly userService = inject(UserService);
  private readonly customerService = inject(CustomerService);
  private readonly toastr = inject(ToastrService);
  private readonly dialog = inject(MatDialog);
  private readonly rbacService = inject(RbacService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly unsavedHost = new UnsavedChangesHostHelper(
    inject(UnsavedChangesTrackerService),
    this.dialog,
    this.router
  );
  private fullColumnDefs: ColDef<UserListRow>[] | null = null;
  private syncingHeaderColumnFilters = false;

  paginationPageSize = AG_GRID_DEFAULT_PAGE_SIZE;
  readonly pageSizeOptions = AG_GRID_PAGE_SIZE_OPTIONS;
  /** Matches [rowHeight] on the grid — used for host height sizing. */
  readonly gridRowHeightPx = 56;
  /** Host height from loaded rows — page scrolls, not the grid. */
  gridHostHeightPx = AG_GRID_HEADER_AND_PAGING_PX + AG_GRID_EMPTY_BODY_ROW_PX;
  gridApi: any;
  currentPage: number = 0;
  total_number_of_records = 0;
  private loadErrorShown = false;

  customerList: any[] = [];
  selectedCustomer = 'All';

  private readonly sessionSortKey = 'userListSortingState';
  private readonly textFilterParams = {
    filterOptions: ['contains'],
    defaultOption: 'contains',
    trimInput: true,
    maxNumConditions: 1,
    suppressFilterButton: true,
  };
  private readonly dateColumnFilterParams = createUtcDateFilterParams();

  payloadOfGetUsersList: UserListSearchPayload = {
    page: 0,
    size: AG_GRID_DEFAULT_PAGE_SIZE,
    sorts: [{ field: 'createdAt', direction: 'desc' }],
    filters: [{ field: '', operator: '', value: '' }],
  };

  canDeactivate(): Promise<boolean> {
    return this.unsavedHost.canDeactivate();
  }

  @HostListener('window:beforeunload', ['$event'])
  onWindowBeforeUnload(event: BeforeUnloadEvent): void {
    this.unsavedHost.onBeforeUnload(event);
  }

  ngOnDestroy(): void {
    this.unsavedHost.clearOnDestroy();
  }

  ngOnInit(): void {
    this.applyRbacToGrid();
    this.rbacService.contextChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyRbacToGrid());
    this.rbacService.ensureContextLoaded().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    const storedSorting = sessionStorage.getItem(this.sessionSortKey);
    if (storedSorting) {
      const sortState: { colId: string; sort: SortDirection } = JSON.parse(storedSorting);
      this.payloadOfGetUsersList.sorts[0].field = sortState.colId;
      this.payloadOfGetUsersList.sorts[0].direction = sortState.sort;
    }

    const storedCustomer = sessionStorage.getItem('userListSelectedCustomer');
    if (storedCustomer) {
      this.selectedCustomer = storedCustomer;
    }

    this.userService.ensureRoleCatalogLoaded().subscribe();
    this.userService.ensureCustomerCatalogLoaded().subscribe();
    this.loadCustomersForDropdown();
  }

  private loadCustomersForDropdown(): void {
    this.customerService
      .getAllCustomer({
        page: 0,
        size: 500,
        filters: [{ field: '', operator: '', value: '' }],
        sorts: [{ field: 'customerName', direction: 'asc' }],
      })
      .subscribe((res) => {
        this.customerList = res?.data?.content ?? [];
      });
  }

  readonly defaultColDef: ColDef = {
    sortable: true,
    sortingOrder: ['asc', 'desc'],
    resizable: true,
    floatingFilter: true,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
  };

  /** Single-token Name search must OR firstName/lastName (BE ANDs filters). */
  private nameOrSearchToken: string | null = null;

  dataSource: IDatasource = {
    getRows: (params: IGetRowsParams) => {
      this.sortForAgGrid(params.sortModel);
      this.filterForAgGrid(params.filterModel);

      this.payloadOfGetUsersList.page = Math.floor(
        params.startRow / this.paginationPageSize
      );
      this.payloadOfGetUsersList.size = this.paginationPageSize;

      const onError = (err: unknown) => {
        if (!this.loadErrorShown) {
          this.loadErrorShown = true;
          const e = err as { error?: { detail?: string; message?: string }; message?: string };
          const msg =
            e?.error?.detail ??
            e?.error?.message ??
            e?.message ??
            'Failed to load users. Please try again.';
          this.toastr.error(msg, 'Load Failed', {
            timeOut: 7000,
            closeButton: true,
            progressBar: true,
          });
        }
        this.syncGridHostHeight(0);
        params.failCallback();
      };

      const onSuccess = (rows: UserListRow[], total: number) => {
        this.loadErrorShown = false;
        this.total_number_of_records = total;
        this.syncGridHostHeight(rows.length);
        params.successCallback(rows, total);
      };

      // Single-token Name filter: match firstName OR lastName (e.g. "Anderson").
      if (this.nameOrSearchToken) {
        const token = this.nameOrSearchToken;
        const base = this.payloadOfGetUsersList;
        const otherFilters = (base.filters ?? []).filter(
          (f) => f.field != null && String(f.field).trim() !== ''
        );

        forkJoin({
          byFirst: this.userService.searchUsers({
            ...base,
            filters: [
              ...otherFilters,
              { field: 'firstName', operator: 'like', value: token },
            ],
          }),
          byLast: this.userService.searchUsers({
            ...base,
            filters: [
              ...otherFilters,
              { field: 'lastName', operator: 'like', value: token },
            ],
          }),
        }).subscribe({
          next: ({ byFirst, byLast }) => {
            const byId = new Map<number, UserListRow>();
            for (const row of [...(byFirst.content ?? []), ...(byLast.content ?? [])]) {
              byId.set(row.userId, row);
            }
            const merged = Array.from(byId.values());
            const total = Math.max(
              byFirst.totalElements ?? 0,
              byLast.totalElements ?? 0,
              merged.length
            );
            onSuccess(merged.slice(0, this.paginationPageSize), total);
          },
          error: onError,
        });
        return;
      }

      this.userService.searchUsers(this.payloadOfGetUsersList).subscribe({
        next: (page) => {
          onSuccess(page?.content ?? [], page?.totalElements ?? 0);
        },
        error: onError,
      });
    },
  };

  /**
   * Size host to the loaded page of rows (matches [rowHeight]="56").
   * Full content height so the grid does not need an internal vertical scrollbar.
   */
  private syncGridHostHeight(loadedRowCount: number): void {
    const rows = Number.isFinite(loadedRowCount) && loadedRowCount > 0 ? loadedRowCount : 0;
    const bodyPx =
      rows > 0 ? rows * this.gridRowHeightPx : AG_GRID_EMPTY_BODY_ROW_PX;
    const contentPx = AG_GRID_HEADER_AND_PAGING_PX + bodyPx;
    const minHostPx = AG_GRID_HEADER_AND_PAGING_PX + AG_GRID_EMPTY_BODY_ROW_PX;
    this.gridHostHeightPx = Math.max(minHostPx, contentPx);
  }

  columnDefs: ColDef<UserListRow>[] = [
    {
      headerName: 'Name',
      field: 'username',
      flex: 1,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
    },
    {
      headerName: 'Email',
      field: 'email',
      flex: 1.4,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
    },
    {
      headerName: 'Customer Name',
      field: 'customerName',
      colId: 'customerName',
      flex: 1.1,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
    },
    {
      headerName: 'User Role',
      field: 'userRole',
      colId: 'userRole',
      width: 200,
      minWidth: 200,
      maxWidth: 200,
      flex: 0,
      suppressSizeToFit: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      valueGetter: (params) =>
        params.data?.userRoles?.join(', ') ?? params.data?.userRole ?? '',
      cellRenderer: UserRolesCellRendererComponent,
    },
    {
      headerName: 'Status',
      field: 'status',
      flex: 0.8,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        maxNumConditions: 1,
        suppressFilterButton: true,
      },
      suppressFloatingFilterButton: true,
      floatingFilterComponent: UserStatusFloatingFilter,
      cellRenderer: (params: { value?: string }) => params.value ?? 'N/A',
    },
    {
      headerName: 'Created By',
      field: 'createdBy',
      flex: 1.2,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
    },
    withDateColumnLayout({
      headerName: 'Created At',
      field: 'createdAt',
      filter: 'agDateColumnFilter',
      floatingFilter: true,
      filterParams: this.dateColumnFilterParams,
      cellRenderer: (params: any) => formatUtcDateTimeCell(params?.data?.createdAt),
    }),
    withDateColumnLayout({
      headerName: 'Updated At',
      field: 'updatedAt',
      filter: 'agDateColumnFilter',
      floatingFilter: true,
      filterParams: this.dateColumnFilterParams,
      cellRenderer: (params: any) => formatUtcDateTimeCell(params?.data?.updatedAt),
    }),
    {
      headerName: 'Action',
      colId: 'action',
      flex: 0.7,
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellRenderer: RolesActionCellRendererComponent,
      cellRendererParams: {
        editPermission: RbacPermissions.UserMgmt_UserList_Edit,
        deletePermission: RbacPermissions.UserMgmt_UserList_Delete,
        onEdit: (row: UserListRow) => this.onEditUser(row),
        onDelete: (row: UserListRow) => this.onDeleteUser(row),
      },
    },
  ];

  private applyRbacToGrid(): void {
    if (!this.fullColumnDefs) {
      this.fullColumnDefs = [...this.columnDefs];
    }

    const canEdit = this.rbacService.hasPermission(
      RbacPermissions.UserMgmt_UserList_Edit
    );
    const canDelete = this.rbacService.hasPermission(
      RbacPermissions.UserMgmt_UserList_Delete
    );

    this.columnDefs = this.fullColumnDefs.filter((col) => {
      if (col.colId === 'action') {
        return canEdit || canDelete;
      }
      return true;
    });

    if (this.gridApi) {
      this.gridApi.setGridOption('columnDefs', this.columnDefs);
    }
  }

  contextMenuVisible = false;
  contextMenuPosition = { x: 0, y: 0 };
  selectedCellValue = '';

  getNestedProperty(obj: any, path: string): any {
    return get(obj, path, 'N/A');
  }

  onCellContextMenu(event: any): void {
    event.event.preventDefault();
    const columnField = event.colDef.field;
    let cellValue = 'N/A';
    if (event.colDef.cellRenderer) {
      const cellRenderer = event.colDef.cellRenderer;
      const renderedValue = cellRenderer({
        data: event.data,
        colDef: event.colDef,
        value: event.value,
      });
      if (typeof renderedValue === 'string') {
        cellValue = renderedValue.replace(/<[^>]*>/g, '');
      } else if (renderedValue?.outerText) {
        cellValue = renderedValue.outerText;
      }
    } else {
      cellValue = this.getNestedProperty(event.data, columnField);
    }

    if (
      cellValue === 'N/A' &&
      event.value != null &&
      String(event.value).trim() !== ''
    ) {
      cellValue = String(event.value);
    }

    if (event.node?.data) {
      this.selectedCellValue = cellValue;
      this.contextMenuPosition = {
        x: event.event.clientX,
        y: event.event.clientY,
      };
      this.contextMenuVisible = true;
    } else {
      this.contextMenuVisible = false;
    }
  }

  handleCopy(): void {
    navigator.clipboard
      .writeText(this.selectedCellValue)
      .then(() => {
        this.contextMenuVisible = false;
      })
      .catch((err) => console.error('Failed to copy:', err));
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.contextMenuVisible = false;
  }

  preventDefaultContextMenu(event: MouseEvent): void {
    event.preventDefault();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;

    const storedSorting = sessionStorage.getItem(this.sessionSortKey);
    const sortState: { colId: string; sort: SortDirection } = storedSorting
      ? (JSON.parse(storedSorting) as { colId: string; sort: SortDirection })
      : { colId: 'createdAt', sort: 'desc' };

    this.gridApi.applyColumnState({
      state: [{ colId: sortState.colId, sort: sortState.sort }],
      applyOrder: false,
      defaultState: { sort: null },
    });

    this.applyUserRoleFilterFromQueryParams();
    this.restoreCustomerFilterToGrid();
  }

  /** Header Customer dropdown → Customer Name column filter. */
  onHeaderCustomerChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value || 'All';
    this.selectedCustomer = value;
    sessionStorage.setItem('userListSelectedCustomer', this.selectedCustomer);
    this.applyHeaderCustomerToGrid(this.selectedCustomer === 'All' ? null : this.selectedCustomer);
  }

  private applyHeaderCustomerToGrid(customerName: string | null): void {
    if (!this.gridApi) {
      return;
    }
    const model: Record<string, any> = { ...(this.gridApi.getFilterModel() || {}) };
    if (customerName) {
      model['customerName'] = {
        filterType: 'text',
        type: 'contains',
        filter: customerName,
      };
    } else {
      delete model['customerName'];
    }
    this.syncingHeaderColumnFilters = true;
    this.gridApi.setFilterModel(model);
    queueMicrotask(() => {
      this.syncingHeaderColumnFilters = false;
    });
  }

  private restoreCustomerFilterToGrid(): void {
    if (this.selectedCustomer && this.selectedCustomer !== 'All') {
      this.applyHeaderCustomerToGrid(this.selectedCustomer);
    }
  }

  /** Column floating filters → header Customer dropdown. */
  onGridFilterChanged(): void {
    if (!this.gridApi || this.syncingHeaderColumnFilters) {
      return;
    }
    const model = this.gridApi.getFilterModel() || {};
    const customerFilter = String(model['customerName']?.filter ?? '').trim();
    if (!customerFilter) {
      if (this.selectedCustomer !== 'All') {
        this.selectedCustomer = 'All';
        sessionStorage.setItem('userListSelectedCustomer', 'All');
      }
      return;
    }
    const exact = this.customerList.find((c: any) => c.customerName === customerFilter);
    if (exact && this.selectedCustomer !== exact.customerName) {
      this.selectedCustomer = exact.customerName;
      sessionStorage.setItem('userListSelectedCustomer', this.selectedCustomer);
    }
  }

  /**
   * Seeds the User Role floating filter from `?userRole=` (e.g. Reseller_Admin
   * deep-link from Role & Permission summary card). Clearing the filter in the
   * grid returns to the unfiltered list; query param is consumed after apply.
   */
  private applyUserRoleFilterFromQueryParams(): void {
    if (!this.gridApi) {
      return;
    }

    const roleFilter = String(
      this.route.snapshot.queryParamMap.get('userRole') ??
        this.route.snapshot.queryParamMap.get('role') ??
        ''
    ).trim();

    if (!roleFilter) {
      return;
    }

    const existingModel =
      (this.gridApi.getFilterModel() as Record<string, unknown> | null) ?? {};

    this.gridApi.setFilterModel({
      ...existingModel,
      userRole: {
        filterType: 'text',
        type: 'contains',
        filter: roleFilter,
      },
    });
    this.gridApi.purgeInfiniteCache();

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { userRole: null, role: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onPaginationChanged(event: PaginationChangedEvent): void {
    syncInfiniteRowModelPageSize(
      event,
      this.paginationPageSize,
      (newPageSize) => {
        this.paginationPageSize = newPageSize;
        this.payloadOfGetUsersList.size = newPageSize;
        this.payloadOfGetUsersList.page = 0;
      }
    );
    this.currentPage = event.api.paginationGetCurrentPage();
  }

  private filterForAgGrid(filterModel: Record<string, any> | null | undefined): void {
    const newFilters: UserListFilter[] = [];
    this.nameOrSearchToken = null;

    if (filterModel && Object.keys(filterModel).length > 0) {
      for (const field of Object.keys(filterModel)) {
        const filter = filterModel[field];

        // Name column is FE-built from firstName + lastName (not a BE username column).
        if (field === 'username') {
          const raw = filter?.['filter'];
          if (raw == null || String(raw).trim() === '') {
            continue;
          }
          const nameFilters = this.mapNameFilterToApiFilters(String(raw).trim());
          newFilters.push(...nameFilters);
          continue;
        }

        const mapped = this.mapAgGridFilterToApiFilter(field, filter);
        if (mapped) {
          newFilters.push(mapped);
        }
      }
    }

    if (newFilters.length === 0) {
      newFilters.push({ field: '', operator: '', value: '' });
    }

    this.payloadOfGetUsersList.filters = newFilters;
  }

  /**
   * Map Name filter onto firstName/lastName.
   * Multi-word (e.g. "Rick Anderson"): AND first + last.
   * Single-word (e.g. "Anderson"): OR via nameOrSearchToken + dual API calls.
   */
  private mapNameFilterToApiFilters(value: string): UserListFilter[] {
    const parts = value.split(/\s+/).filter((part) => part.length > 0);
    if (parts.length === 0) {
      return [];
    }
    if (parts.length >= 2) {
      return [
        { field: 'firstName', operator: 'like', value: parts[0] },
        { field: 'lastName', operator: 'like', value: parts.slice(1).join(' ') },
      ];
    }
    this.nameOrSearchToken = parts[0];
    return [];
  }

  private mapAgGridFilterToApiFilter(
    field: string,
    filter: Record<string, unknown>
  ): UserListFilter | null {
    if (field === 'status') {
      const raw = filter?.['filter'];
      if (raw == null || String(raw).trim() === '') return null;
      const selected = String(raw).trim();
      if (selected === 'Active' || selected === 'Inactive') {
        return { field: 'status', operator: 'eq', value: selected };
      }
      return { field: 'status', operator: 'like', value: selected };
    }

    if (field === 'createdAt' || field === 'updatedAt') {
      const mapped = mapAgGridFilterToApi(field, filter);
      if (!mapped) {
        return null;
      }
      return {
        field: mapped.field,
        operator: mapped.operator,
        value: mapped.value as UserListFilter['value'],
      };
    }

    const raw = filter?.['filter'];
    if (raw == null || String(raw).trim() === '') {
      return null;
    }

    return { field, operator: 'like', value: String(raw).trim() };
  }

  private sortForAgGrid(sortModel: any): void {
    if (!sortModel?.[0]?.colId) return;

    sessionStorage.setItem(this.sessionSortKey, JSON.stringify(sortModel[0]));
    const colId = sortModel[0].colId as string;
    const allowedFields = [
      'username',
      'email',
      'customerName',
      'userRole',
      'status',
      'createdBy',
      'createdAt',
      'updatedAt',
    ];

    if (allowedFields.includes(colId)) {
      this.payloadOfGetUsersList.sorts[0].field = colId;
      this.payloadOfGetUsersList.sorts[0].direction = sortModel[0].sort;
    }
  }

  onAddUser(): void {
    const dialogRef = this.dialog.open<
      AddUserModalComponent,
      AddUserDialogData,
      AddUserDialogResult | undefined
    >(AddUserModalComponent, {
      width: '90%',
      maxWidth: '560px',
      restoreFocus: false,
      autoFocus: true,
      closeOnNavigation: false,
      data: { mode: 'add' },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.gridApi?.purgeInfiniteCache();
      }
    });
  }

  private onEditUser(row: UserListRow): void {
    const fallbackCustomerId = this.resolveCustomerIdForEdit(row);
    if (fallbackCustomerId == null) {
      this.toastr.error(
        'Missing customer context for this user. Please filter by customer and try again.',
        'Edit Failed',
        { timeOut: 5000, closeButton: true, progressBar: true }
      );
      return;
    }

    const dialogRef = this.dialog.open<
      AddUserModalComponent,
      AddUserDialogData,
      AddUserDialogResult | undefined
    >(AddUserModalComponent, {
      width: '90%',
      maxWidth: '560px',
      restoreFocus: false,
      autoFocus: true,
      closeOnNavigation: false,
      data: {
        mode: 'edit',
        rowData: row,
        customerId: fallbackCustomerId,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.gridApi?.purgeInfiniteCache();
      }
    });
  }

  private onDeleteUser(row: UserListRow): void {
    const userId = row?.userId;
    if (userId == null || userId <= 0) {
      this.toastr.error('Missing userId — cannot delete this user.', 'Delete Failed', {
        timeOut: 5000,
        closeButton: true,
        progressBar: true,
      });
      return;
    }

    const customerId = this.resolveCustomerIdForEdit(row);
    if (customerId == null) {
      this.toastr.error(
        'Missing customer context for this user. Cannot delete without customerId.',
        'Delete Failed',
        { timeOut: 5000, closeButton: true, progressBar: true }
      );
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteUserComponent, {
      width: '520px',
      restoreFocus: false,
      data: {
        userId,
        username: row.username ?? 'N/A',
        email: row.email !== 'N/A' ? row.email : undefined,
        customerId,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.userService.deleteUser(userId, customerId).subscribe({
        next: () => {
          this.toastr.success(
            `User "${row.username}" has been deleted successfully.`,
            'User Deleted',
            { timeOut: 4000, closeButton: true, progressBar: true }
          );
          this.gridApi?.purgeInfiniteCache();
        },
        error: (err) => {
          const msg =
            err?.error?.detail ??
            err?.error?.message ??
            err?.message ??
            `Failed to delete user "${row.username}". Please try again.`;
          this.toastr.error(msg, 'Delete Failed', {
            timeOut: 6000,
            closeButton: true,
            progressBar: true,
          });
        },
      });
    });
  }

  private resolveCustomerIdForEdit(row: UserListRow): number | string | null {
    if (row.customerAssignmentIds && row.customerAssignmentIds.length > 0) {
      return row.customerAssignmentIds[0];
    }

    if (row.customerId != null && row.customerId !== '') {
      return row.customerId;
    }

    if (row.customerAssignmentId != null && row.customerAssignmentId !== '') {
      return row.customerAssignmentId;
    }

    const payloadFilter = (this.payloadOfGetUsersList.filters ?? []).find(
      (f) => f.field === 'customerId'
    );
    if (
      payloadFilter?.value != null &&
      typeof payloadFilter.value !== 'boolean' &&
      !Array.isArray(payloadFilter.value) &&
      String(payloadFilter.value).trim() !== ''
    ) {
      return payloadFilter.value;
    }

    return null;
  }
}
